import { PlusOutlined } from "@ant-design/icons";
import { Button, Divider, Typography } from "antd";
import React, { useCallback, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { DeleteIcon } from "../../../../../../../assets/svg";
import { Booking } from "../../../../../../../types/bookings";
import { AppButton } from "../../../../../../atoms/Button";
import { DecimalUtils } from "../../../../../../libs/utils";
import { useBookingSelector } from "../../../../hooks/useBookingSelector";
import { type ServiceCharge, useGenerateScheduleBreakdown } from "../api";
import { ServiceChargeBreakdown } from "./ServiceChargeBreakdown";
import { ServiceChargeFields } from "./ServiceChargeFields";
import { useServiceChargeBreakdownStaleness } from "./useServiceChargeBreakdownStaleness";

type ServiceChargeSectionProps = {
  planIndex: number;
  yearNumber: number;
  lastEndDate?: string;
  planStableId?: string;
  onServiceChargeBreakdownStaleChange?: (planId: string, stale: boolean) => void;
};

export const ServiceChargeSection = (props: ServiceChargeSectionProps) => {
  const { planIndex, yearNumber, lastEndDate, planStableId, onServiceChargeBreakdownStaleChange } = props;

  const { isPaymentPlanApprovalSent } = useBookingSelector();

  const { unregister, trigger, setValue, getValues, control } = useFormContext();
  const { bookingDetails } = useSelector((state: any) => state?.newBooking);
  const { leaseId } = (bookingDetails ?? {}) as Booking;

  const [hasRow, setHasRow] = useState(() => {
    const scRows = getValues(`plans[${planIndex}].serviceChargeSchedule`) as ServiceCharge[] | undefined;
    const scPlan = getValues(`plans[${planIndex}].serviceCharge`) as
      | { rentType?: unknown; installments?: unknown }
      | undefined;
    const hasScPlan = Boolean(scPlan?.rentType != null && scPlan?.installments != null);
    return Boolean(scRows?.length || hasScPlan);
  });

  const [serviceChargeBreakdownRows, setServiceChargeBreakdownRows] = useState<ServiceCharge[] | null>(() => {
    const scRows = getValues(`plans[${planIndex}].serviceChargeSchedule`) as ServiceCharge[] | undefined;
    return scRows?.length ? scRows : null;
  });

  const baseFieldName = `plans[${planIndex}].serviceCharge`;
  const serviceCharge = useWatch({ name: baseFieldName });
  const serviceChargeScheduleRows = useWatch({
    control,
    name: `plans[${planIndex}].serviceChargeSchedule`,
  }) as ServiceCharge[] | undefined;

  const { generateScheduleBreakdown, isPendingGenerateScheduleBreakdown } = useGenerateScheduleBreakdown();

  const { onBreakdownSuccess, onBreakdownCleared } = useServiceChargeBreakdownStaleness({
    serviceCharge,
    planStableId,
    serviceChargeBreakdownRows,
    onStaleChange: onServiceChargeBreakdownStaleChange,
  });

  const handleAdd = () => setHasRow(true);

  const handleRemove = () => {
    setHasRow(false);
    setServiceChargeBreakdownRows(null);
    onBreakdownCleared();
    unregister(`plans[${planIndex}].serviceCharge`);
    setValue(`plans[${planIndex}].serviceChargeSchedule`, [], { shouldDirty: false, shouldValidate: false });
  };

  const handleServiceChargeRowsChange = useCallback(
    (updatedRows: ServiceCharge[]) => {
      setValue(`plans[${planIndex}].serviceChargeSchedule`, updatedRows, {
        shouldDirty: false,
        shouldValidate: false,
      });
    },
    [planIndex, setValue],
  );

  const handleGenerateBreakdown = async () => {
    if (!leaseId) return;

    const rentType = serviceCharge?.rentType?.value;
    const isPerSqFt = rentType === "areaSqft";
    const isPercentage = rentType === "percentage";

    const isValid = await trigger();
    if (!isValid) return;

    const hasLastEndDate = Boolean(yearNumber > 1 && lastEndDate);

    const data = await generateScheduleBreakdown({
      leaseId,
      generate: "serviceCharge",
      serviceCharge: {
        noOfYear: yearNumber,
        installments: serviceCharge?.installments?.value,
        rentType: serviceCharge?.rentType?.value,
        ...(isPerSqFt
          ? { rentValue: serviceCharge?.chargeValue, unitAreaInSqft: serviceCharge?.unitAreaInSqft?.toString() }
          : {}),
        ...(isPercentage ? { percentageValue: serviceCharge?.chargeValue } : {}),
        annualRent: serviceCharge?.annualRent,
        ...(hasLastEndDate ? { lastEndDate } : {}),
      },
    });

    setServiceChargeBreakdownRows(data);
    setValue(`plans[${planIndex}].serviceChargeSchedule`, data, { shouldDirty: false, shouldValidate: false });
    onBreakdownSuccess(getValues(`plans[${planIndex}].serviceCharge`));
  };

  const serviceChargeRowsForTable = serviceChargeBreakdownRows ?? serviceChargeScheduleRows;
  const hasExistingServiceCharge = Boolean(
    hasRow ||
    serviceChargeRowsForTable?.length ||
    serviceCharge?.rentType != null ||
    serviceCharge?.installments != null ||
    serviceCharge?.annualRent != null,
  );

  if (isPaymentPlanApprovalSent && !hasExistingServiceCharge) return null;

  return (
    <>
      <Divider />

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "1rem" }}>
        <Typography.Title level={5}>Service Charge</Typography.Title>
        {!isPaymentPlanApprovalSent && hasRow && (
          <AppButton variant="text" icon={<DeleteIcon />} onClick={handleRemove} />
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateRows: hasRow ? "1fr" : "0fr",
          transition: "grid-template-rows 0.35s ease",
        }}
      >
        <div style={{ overflow: "hidden" }}>
          {hasRow && (
            <ServiceChargeFields
              planIndex={planIndex}
              isPendingGenerateScheduleBreakdown={isPendingGenerateScheduleBreakdown}
              onGenerateBreakdown={handleGenerateBreakdown}
            />
          )}
        </div>
      </div>

      {serviceChargeBreakdownRows && serviceChargeRowsForTable && (
        <ServiceChargeBreakdown
          rows={serviceChargeRowsForTable}
          totalServiceCharge={DecimalUtils.toNumber(serviceCharge?.annualRent)}
          onRowsChange={handleServiceChargeRowsChange}
        />
      )}

      {!hasRow && !isPaymentPlanApprovalSent && (
        <Button type="dashed" block icon={<PlusOutlined />} onClick={handleAdd}>
          <Typography.Text className="ag-primary fontWeight600">Add Service Charge</Typography.Text>
        </Button>
      )}
    </>
  );
};
